import {Injectable} from "@angular/core";
import {ApiService} from "./api.service";
import {Address, Group, HealthcareParty, PaginatedListUser, TypedValueObject, User} from "@icure/api";
import {
  AssistantInfos,
  DESKTOP_API_CREDENTIALS_PROP,
  LicenceInfos,
  PropertyParser,
  UserInfo
} from "../models/user.model";
import * as _ from "lodash";
import {
  INVOICING_PREFERENCE,
  KEYS_PROP,
  LANGUAGE,
} from '../../app/private/database/database-user/database-user.component';
import {HcpService} from "./hcp.service";
import AddressTypeEnum = Address.AddressTypeEnum;
import {
  isDoctor,
  isMidwife,
  isNurse,
  isPhysiotherapist,
  isSpeechTherapist,
  physicianSpecialityList
} from "../utils/speciality-utils";
import {PropertyStub} from "@icure/api/icc-api/model/PropertyStub";
import {DesktopApiCredentials} from "../models/desktop.model";
import { LoggerService } from '../../app/shared/logger/services/logger.service';

type PreferenceGroup = {
  listOfPreferences?: Array<{ preference?: string; value?: string }>;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private postalCodeInfos = [
    {
      min: 1000,
      max: 1299,
      counterName: 'bxl',
    },
    {
      min: 1300,
      max: 1499,
      counterName: 'wal',
    },
    {
      min: 1500,
      max: 1999,
      counterName: 'fla',
    },
    {
      min: 2000,
      max: 2999,
      counterName: 'fla',
    },
    {
      min: 3000,
      max: 3499,
      counterName: 'fla',
    },
    {
      min: 3500,
      max: 3999,
      counterName: 'fla',
    },
    {
      min: 4000,
      max: 4999,
      counterName: 'wal',
    },
    {
      min: 5000,
      max: 5680,
      counterName: 'wal',
    },
    {
      min: 6000,
      max: 6599,
      counterName: 'wal',
    },
    {
      min: 6600,
      max: 6999,
      counterName: 'wal',
    },
    {
      min: 7000,
      max: 7999,
      counterName: 'wal',
    },
    {
      min: 8000,
      max: 8999,
      counterName: 'fla',
    },
    {
      min: 9000,
      max: 9999,
      counterName: 'fla',
    },
  ];

  constructor(
    private api: ApiService,
    private hcpService: HcpService,
    private logger: LoggerService,
  ) {}

  public async getUserInGroup(
    groupId: string,
    startKey: string | null = null,
    startKeyDocId: string | null = null,
    limit: number = 100,
    // Legacy callers (e.g. reporting.service) chain `.catch()` onto the result and
    // rely on it being loosely typed, so the return type stays `any` here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    // eslint-disable-line @typescript-eslint/no-explicit-any
    const allUsers = [];
    let pageEnd = false;
    while (!pageEnd) {
      const userPage: PaginatedListUser | undefined =
        await this.api.userApi?.listUsersInGroup(
          groupId,
          startKey!,
          startKeyDocId!,
          limit,
        );
      pageEnd = !userPage?.nextKeyPair;
      if (!pageEnd) {
        startKey = userPage?.nextKeyPair?.startKey || null;
        startKeyDocId = userPage?.nextKeyPair?.startKeyDocId || null;
      }
      allUsers.push(userPage?.rows);
    }
    return allUsers.flat(Infinity);
  }

  public async getTokenForUser(
    groupId: string,
    userId: string,
  ): Promise<string> {
    return this.api.userApi.getTokenInGroup(
      groupId,
      userId,
      groupId + userId,
      undefined,
      1800,
    );
  }

  public async getFullUserInfos(groups: Group[]): Promise<UserInfo[]> {
    let prom: Promise<unknown> = Promise.resolve();
    const listOfUsers: Array<UserInfo> = [];
    _.chunk(groups, 25).map(
      (groupChunk: Group[]) =>
        (prom = prom.then(() =>
          Promise.all(
            groupChunk.map((group: Group) =>
              this.getUserInGroup(group.id!, null, null, 500)
                .then(async (users) => {
                  const hcps: HealthcareParty[] =
                    (await this.hcpService.getHcpsInGroup(
                      group.id!,
                      users?.map((usr: User) => usr.healthcarePartyId!),
                    )) || [];
                  users?.map((user: User) => {
                    const hcp: HealthcareParty =
                      hcps?.find(
                        (hcp) => hcp?.id === user?.healthcarePartyId,
                      ) || {};
                    const hcpAdr: Address =
                      hcp?.addresses?.find(
                        (adr) => adr?.addressType === AddressTypeEnum.Work,
                      ) || {};
                    const creationDate: Date = new Date(user.created!);
                    const hasKeys: boolean =
                      hcp?.hcPartyKeys || hcp?.publicKeysForOaepWithSha256
                        ? !!Object?.keys(hcp?.hcPartyKeys as object)?.length ||
                          !!hcp?.publicKeysForOaepWithSha256?.length
                        : false;
                    let parsedInvoicing: {
                      invoicing?: PreferenceGroup[];
                    } | null = null;
                    try {
                      parsedInvoicing = JSON.parse(
                        this.getInvoicingPreference(user.properties!) || '',
                      );
                    } catch {
                      /* ignore malformed invoicing preferences */
                    }
                    const invoicingPreferences: PreferenceGroup[] =
                      parsedInvoicing?.invoicing || [];
                    const convention: string =
                      hcp?.convention?.toString() ?? '';
                    const conventionHr: string =
                      convention === '1'
                        ? 'Conventionné'
                        : convention === '9'
                          ? 'Non conventionné'
                          : convention === '2'
                            ? 'Partiellement'
                            : '-';
                    const defaultTarification: string =
                      this.getPreferenceByKey(
                        invoicingPreferences,
                        'defaultTarification',
                      ) || '';
                    const defaultTarificationHr: string =
                      defaultTarification === 'official'
                        ? 'Officiel'
                        : defaultTarification === 'no-tm'
                          ? 'Pas de TM'
                          : defaultTarification === 'customPrice'
                            ? 'Custom'
                            : defaultTarification === 'roundedPrice'
                              ? 'Arrondi'
                              : '-';
                    listOfUsers.push({
                      groupId: group.id || '',
                      groupPassword: group.password || '',
                      userId: user.id || '',
                      creationDate: creationDate,
                      creationDateHr: `${creationDate.getDate().toString().padStart(2, '0')}/${(creationDate.getMonth() + 1).toString().padStart(2, '0')}/${creationDate.getFullYear()}`,
                      login: user.login || '',
                      ssin: hcp?.ssin || '',
                      nihii: hcp?.nihii || hcp?.options?.['inami'] || '',
                      isKeyGenerate:
                        user?.properties?.find(
                          (prop) => prop?.type?.identifier === KEYS_PROP,
                        )?.typedValue?.booleanValue || false,
                      adr: {
                        street: hcpAdr?.street || '',
                        houseNumber: hcpAdr?.houseNumber || '',
                        city: hcpAdr?.city || '',
                        country: hcpAdr?.country || '',
                        postalCode: hcpAdr?.postalCode || '',
                        region:
                          hcpAdr?.postalCode &&
                          !isNaN(Number(hcpAdr?.postalCode))
                            ? this.getDistrictOfPatient(
                                Number(hcpAdr?.postalCode),
                              )
                            : '',
                      },
                      name: group.name,
                      speciality: this.getUserSpeciality(
                        hcp?.nihii || hcp?.options?.['inami'] || '',
                      ),
                      language:
                        user?.properties?.find(
                          (prop) => prop?.type?.identifier === LANGUAGE,
                        )?.typedValue?.stringValue || '',
                      convention: conventionHr,
                      defaultTarification: defaultTarificationHr,
                      steps: {
                        step_1: !!user?.passwordHash,
                        step_2: !!hcp?.nihii,
                        step_3: !!hcpAdr?.postalCode,
                        step_4: !!hcp?.bankAccount,
                        step_5: hasKeys || false,
                      },
                      statusHr: !user?.passwordHash
                        ? 'Inactif'
                        : hasKeys
                          ? 'Actif'
                          : !!user?.passwordHash && !hasKeys
                            ? 'Bloqué'
                            : '',
                    });
                  });
                })
                .catch((e) => this.logger.log(e)),
            ),
          ).catch((e) => this.logger.log(e)),
        )),
    );
    return prom.then(() => {
      return listOfUsers;
    });
  }

  public getUserSpeciality(nihii: string): string {
    let speciality: string = '';
    if (isDoctor(nihii)) {
      speciality =
        physicianSpecialityList.find(
          (spe) => spe.code === nihii.substring(8, 11),
        )?.speciality?.fr || '';
    } else if (isNurse(nihii)) {
      speciality = 'Infirmier(e)';
    } else if (isPhysiotherapist(nihii)) {
      speciality = 'Kiné';
    } else if (isMidwife(nihii)) {
      speciality = 'Sage-femme';
    } else if (isSpeechTherapist(nihii)) {
      speciality = 'Logopède';
    } else if (!nihii) {
      speciality = 'Indéfini';
    } else {
      speciality = 'Autre';
    }
    return speciality;
  }

  public getDistrictOfPatient(postalCode: number): string {
    return (
      this.postalCodeInfos?.find(
        (pci) => postalCode >= pci.min && postalCode <= pci.max,
      )?.counterName ?? ''
    );
  }

  public getLicenceInfos(property: PropertyStub): LicenceInfos {
    let licenceInfos: LicenceInfos = {
      licenceType: '',
      startDate: '',
      endDate: '',
    };

    if (property) {
      try {
        const licence = property?.typedValue?.stringValue;
        const licenceValue = licence?.split('-medi-')?.[0];
        const parsedLicence = JSON.parse(licenceValue!);
        licenceInfos = {
          startDate: parsedLicence?.startDate,
          endDate: parsedLicence?.endDate,
          licenceType: parsedLicence?.licenceType,
        };
      } catch {
        this.logger.warn('Impossible to parse user licence');
      }
    }
    return licenceInfos;
  }

  public getAssistantInfos(property: PropertyStub): AssistantInfos {
    let assistantInfos: AssistantInfos = {
      isNextStructureTheSame: false,
      nextStatus: '',
      nextSupervisorId: '',
      validationDate: '',
    };

    if (property) {
      try {
        const assistantInvoicing = property?.typedValue?.stringValue;
        const parsedAssistantInvoicing = JSON.parse(assistantInvoicing!);

        assistantInfos = {
          isNextStructureTheSame:
            parsedAssistantInvoicing?.isNextStructureTheSame === 'true',
          nextStatus: parsedAssistantInvoicing?.nextStatus,
          nextSupervisorId: parsedAssistantInvoicing?.nextSupervisorId,
          validationDate: parsedAssistantInvoicing?.validationDate,
        };
      } catch {
        this.logger.warn('Impossible to parse assistant info');
      }
    }
    return assistantInfos;
  }

  public isUserHasLicence(property: PropertyStub): boolean {
    let licenceValue: string | undefined = '';
    if (property) {
      try {
        const licence = property?.typedValue?.stringValue;
        licenceValue = licence?.split('-medi-')?.[0];
      } catch {
        this.logger.warn('Impossible to parse user licence');
      }
    }
    return !!licenceValue;
  }

  public async getDesktopApiCredentials(
    env: string,
  ): Promise<DesktopApiCredentials> {
    let desktopApiCredentials: DesktopApiCredentials[] = [];
    try {
      const user: User | undefined = await this.api.userApi?.getCurrentUser();
      const desktopApiCredentialsProp: PropertyStub | undefined =
        user?.properties?.find(
          (prop) => prop?.type?.identifier === DESKTOP_API_CREDENTIALS_PROP,
        );
      desktopApiCredentials = JSON.parse(
        desktopApiCredentialsProp?.typedValue?.stringValue || '[]',
      );
    } catch {
      this.logger.warn('Impossible to get desktop api credentials from user');
    }
    return desktopApiCredentials?.find(
      (cred) => cred?.env === env,
    ) as DesktopApiCredentials;
  }

  public async addUserInGroup(
    groupId: string,
    user: {
      healthcarePartyId: string;
      roles: string[] | undefined;
      name:
        | `${string} ${string}`
        | `${string} undefined`
        | `undefined ${string}`
        | 'undefined undefined';
      id: string;
      login: string;
      autoDelegations?: { [key: string]: Array<string> };
      type: User.TypeEnum;
      email?: string;
      passwordHash: string;
      applicationTokens: { [key: string]: string };
      status: User.StatusEnum;
    },
  ) {
    return this.api.userApi?.createUserInGroup(groupId, user);
  }

  private getInvoicingPreference(
    properties: Array<PropertyStub>,
  ): string | undefined {
    return this.getProperty(
      INVOICING_PREFERENCE,
      TypedValueObject.TypeEnum.STRING,
      properties,
    ) as string | undefined;
  }

  private getProperty(
    name: string,
    type: TypedValueObject.TypeEnum,
    properties: Array<PropertyStub>,
  ): unknown {
    const property: PropertyStub = this.findProperty(name, properties);
    return property && this.getValueOfProperty(property, type);
  }

  private findProperty(
    name: string,
    properties: Array<PropertyStub>,
  ): PropertyStub {
    return properties?.find((p) => p.type?.identifier === name) || {};
  }

  private getValueOfProperty(
    { typedValue }: PropertyStub,
    type: TypedValueObject.TypeEnum,
  ): unknown {
    const parser = PropertyParser[type || typedValue?.type] || _.constant(null);
    return parser(typedValue!);
  }

  private getPreferenceByKey(
    preferences: Array<PreferenceGroup> | null | undefined,
    key: string,
  ): string | null {
    if (!preferences || !Object.keys(preferences).length || !key) return null;

    const targetValue = preferences
      .flatMap((p) =>
        (p?.listOfPreferences || []).filter((pref) => pref?.preference === key),
      )
      .filter(Boolean)[0]?.value;

    return targetValue ?? null;
  }
}
